"use client";

import { useEffect, useState, useCallback } from "react";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useCart } from "@/lib/cart-context";
import { getProductImageUrl, formatPriceValue } from "@/lib/utils";
import { CheckCircle, X, ShoppingCart, ChevronLeft, ChevronRight } from "lucide-react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
} from "@/components/ui/carousel";

interface RelatedProduct {
  id: number;
  title: string;
  slug: string;
  image_local: string | null;
  price_ht: number | null;
  display_rent_price_ht: number | null;
  store_name: string | null;
  selling_type: string | null;
}

export function AddToCartModal() {
  const router = useRouter();
  const { lastAddedItem, dismissCartNotification, cartCount } = useCart();
  const [relatedProducts, setRelatedProducts] = useState<RelatedProduct[]>([]);
  const [loadingRelated, setLoadingRelated] = useState(false);

  // Fetch related products when an item is added
  useEffect(() => {
    if (!lastAddedItem) {
      setRelatedProducts([]);
      return;
    }

    setLoadingRelated(true);
    fetch(`/api/products/related?productId=${lastAddedItem.productId}&limit=10`)
      .then((r) => r.json())
      .then((data) => {
        setRelatedProducts(data.products || []);
      })
      .catch(() => {
        setRelatedProducts([]);
      })
      .finally(() => setLoadingRelated(false));
  }, [lastAddedItem]);

  // ESC to close
  useEffect(() => {
    if (!lastAddedItem) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") dismissCartNotification();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [lastAddedItem, dismissCartNotification]);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (lastAddedItem) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [lastAddedItem]);

  const handleGoToCart = useCallback(() => {
    dismissCartNotification();
    router.push("/panier");
  }, [dismissCartNotification, router]);

  if (!lastAddedItem) return null;

  const itemPrice = lastAddedItem.sellingType === "RENT"
    ? lastAddedItem.unitPriceHt
    : lastAddedItem.unitPriceHt;

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black/50 z-[9998]"
        onClick={dismissCartNotification}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 pointer-events-none">
        <div
          className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto pointer-events-auto animate-in zoom-in-95 fade-in duration-200"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header bar */}
          <div className="bg-secondary px-5 py-3 flex items-center justify-between rounded-t-2xl">
            <div className="flex items-center gap-2 text-white text-sm font-medium">
              <CheckCircle className="h-5 w-5" />
              Produit ajouté au panier
            </div>
            <button
              onClick={dismissCartNotification}
              className="text-white/80 hover:text-white transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Added product info */}
          <div className="p-5 flex gap-5 border-b">
            {/* Product image */}
            <div className="relative w-40 h-40 flex-shrink-0 bg-white rounded-xl overflow-hidden border">
              {lastAddedItem.productImage ? (
                <Image
                  src={getProductImageUrl(lastAddedItem.productImage)}
                  alt={lastAddedItem.productTitle}
                  fill
                  className="object-contain p-3"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gray-50">
                  <ShoppingCart className="h-8 w-8 text-gray-300" />
                </div>
              )}
            </div>

            {/* Product details */}
            <div className="flex-1 min-w-0 flex flex-col justify-center">
              <h3 className="text-secondary font-semibold text-lg leading-tight line-clamp-2">
                {lastAddedItem.productTitle}
              </h3>
              {lastAddedItem.storeName && (
                <p className="text-xs text-gray-400 mt-1 uppercase">
                  {lastAddedItem.storeName}
                </p>
              )}
              <p className="text-2xl font-bold text-gray-900 mt-3">
                {formatPriceValue(itemPrice)} €
                {lastAddedItem.sellingType === "RENT" && (
                  <span className="text-sm font-normal text-gray-500 ml-1">HT/jour</span>
                )}
              </p>
              {lastAddedItem.sellingType !== "RENT" && (
                <p className="text-xs text-gray-400">HT</p>
              )}
            </div>
          </div>

          {/* Related products carousel */}
          {(relatedProducts.length > 0 || loadingRelated) && (
            <div className="px-5 py-5">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-secondary font-semibold">
                  Vous pourriez également apprécier...
                </h4>
              </div>

              {loadingRelated ? (
                <div className="flex items-center justify-center py-8">
                  <div className="h-6 w-6 border-2 border-secondary border-t-transparent rounded-full animate-spin" />
                </div>
              ) : (
                <Carousel
                  opts={{ align: "start", loop: false }}
                  className="w-full"
                >
                  <CarouselContent className="-ml-3">
                    {relatedProducts.map((product) => (
                      <CarouselItem
                        key={product.id}
                        className="pl-3 basis-1/2 sm:basis-1/3 md:basis-1/4"
                      >
                        <Link
                          href={`/produits/${product.slug}`}
                          onClick={dismissCartNotification}
                          className="block bg-white rounded-xl border hover:shadow-md transition-shadow group overflow-hidden"
                        >
                          <div className="aspect-square relative bg-white">
                            <Image
                              src={getProductImageUrl(product.image_local)}
                              alt={product.title}
                              fill
                              className="object-contain p-3"
                            />
                          </div>
                          <div className="p-3 bg-gray-50">
                            <p className="text-[11px] text-gray-400 uppercase">
                              {product.store_name || "TOOLOUER"}
                            </p>
                            <h5 className="font-medium text-xs line-clamp-2 mt-0.5 group-hover:text-secondary leading-tight">
                              {product.title}
                            </h5>
                            <p className="text-secondary font-bold text-sm mt-2">
                              {product.display_rent_price_ht
                                ? `${formatPriceValue(product.display_rent_price_ht)} €`
                                : product.price_ht
                                  ? `${formatPriceValue(product.price_ht)} €`
                                  : ""}
                            </p>
                          </div>
                        </Link>
                      </CarouselItem>
                    ))}
                  </CarouselContent>
                  {relatedProducts.length > 4 && (
                    <>
                      <CarouselPrevious className="-left-3 h-8 w-8" />
                      <CarouselNext className="-right-3 h-8 w-8" />
                    </>
                  )}
                </Carousel>
              )}
            </div>
          )}

          {/* CTA buttons */}
          <div className="px-5 pb-5 flex gap-3">
            <button
              onClick={dismissCartNotification}
              className="flex-1 py-3 px-4 border-2 border-secondary text-secondary font-medium rounded-xl hover:bg-secondary/5 transition-colors text-sm"
            >
              Poursuivre votre commande
            </button>
            <button
              onClick={handleGoToCart}
              className="flex-1 py-3 px-4 bg-secondary text-white font-medium rounded-xl hover:bg-secondary/90 transition-colors text-sm"
            >
              Voir mon panier
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
